import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '@/App';
import axios from 'axios';
import ProBadge from '@/components/ProBadge';
import ProUpgradeCard from '@/components/ProUpgradeCard';
import {
  Sparkles, Brain, TrendingUp, TrendingDown, AlertTriangle, CheckCircle,
  ChevronDown, ChevronUp, Calendar, Loader2, RefreshCw, Lock,
  Zap, Heart, Target, Clock, BarChart3, Star, ArrowRight, Download
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];

// ── Animated number counter ──
const CountUp = ({ to, suffix = '' }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = Math.ceil(to / 40);
    const t = setInterval(() => {
      start = Math.min(start + step, to);
      setVal(start);
      if (start >= to) clearInterval(t);
    }, 20);
    return () => clearInterval(t);
  }, [to]);
  return <>{val}{suffix}</>;
};

// ── Burnout ring ──
const BurnoutRing = ({ score }) => {
  const color = score < 25 ? '#22c55e' : score < 50 ? '#eab308' : score < 75 ? '#f97316' : '#ef4444';
  const label = score < 25 ? 'Healthy' : score < 50 ? 'Moderate' : score < 75 ? 'High Risk' : 'Critical';
  const r = 46, circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative w-28 h-28">
        <svg width="112" height="112" className="rotate-[-90deg]">
          <circle cx="56" cy="56" r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="9"/>
          <circle cx="56" cy="56" r={r} fill="none" stroke={color} strokeWidth="9"
            strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
            style={{ transition: 'stroke-dasharray 1.2s cubic-bezier(0.16,1,0.3,1)' }}/>
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold text-white">{score}</span>
          <span className="text-[9px] text-gray-400">/100</span>
        </div>
      </div>
      <span className="text-xs font-semibold" style={{ color }}>{label}</span>
    </div>
  );
};

// ── Section with expand/collapse ──
const ReportSection = ({ icon: Icon, title, color = '#00D9FF', children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="glass-card overflow-hidden mb-4">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-6 py-4 hover:bg-white/2 transition-colors text-left">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: `${color}18`, border: `1px solid ${color}30` }}>
          <Icon size={15} style={{ color }} />
        </div>
        <span className="flex-1 text-sm font-semibold text-white">{title}</span>
        {open ? <ChevronUp size={15} className="text-gray-500"/> : <ChevronDown size={15} className="text-gray-500"/>}
      </button>
      {open && <div className="px-6 pb-6 border-t border-white/5 pt-4">{children}</div>}
    </div>
  );
};

// ── Pattern pill ──
const PatternPill = ({ text, type }) => {
  const cfg = {
    positive: { bg: 'rgba(34,197,94,0.08)', border: 'rgba(34,197,94,0.2)', icon: CheckCircle, color: '#22c55e' },
    warning:  { bg: 'rgba(249,115,22,0.08)', border: 'rgba(249,115,22,0.2)', icon: AlertTriangle, color: '#f97316' },
  }[type];
  const Icon = cfg.icon;
  return (
    <div className="flex items-start gap-3 p-3 rounded-xl mb-2"
      style={{ background: cfg.bg, border: `1px solid ${cfg.border}` }}>
      <Icon size={14} style={{ color: cfg.color, flexShrink: 0, marginTop: 2 }} />
      <p className="text-sm text-gray-200">{text}</p>
    </div>
  );
};

// ── Action item ──
const ActionItem = ({ text, index, color = '#00D9FF' }) => (
  <div className="flex items-start gap-3 py-3 border-b border-white/5 last:border-0">
    <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5"
      style={{ background: `${color}18`, color, border: `1px solid ${color}30` }}>
      {index}
    </div>
    <p className="text-sm text-gray-300 leading-relaxed">{text}</p>
  </div>
);

// ── Stat card ──
const StatCard = ({ label, value, suffix, icon: Icon, color }) => (
  <div className="glass-card p-4 flex flex-col items-center text-center gap-2">
    <div className="w-9 h-9 rounded-xl flex items-center justify-center"
      style={{ background: `${color}18`, border: `1px solid ${color}25` }}>
      <Icon size={16} style={{ color }} />
    </div>
    <p className="text-2xl font-bold text-white"><CountUp to={typeof value === 'number' ? value : 0} suffix={suffix || ''}/></p>
    <p className="text-xs text-gray-400">{label}</p>
  </div>
);

// ── Loading shimmer ──
const Shimmer = () => (
  <div className="space-y-4 animate-pulse">
    {[200, 160, 140, 180].map((w, i) => (
      <div key={i} className="h-32 rounded-2xl" style={{ background: 'rgba(255,255,255,0.04)', width: '100%' }}/>
    ))}
  </div>
);

// ── Typewriter effect for long text ──
const TypeWriter = ({ text, speed = 18 }) => {
  const [displayed, setDisplayed] = useState('');
  useEffect(() => {
    setDisplayed('');
    let i = 0;
    const t = setInterval(() => {
      if (i < text.length) { setDisplayed(text.slice(0, i + 1)); i++; }
      else clearInterval(t);
    }, speed);
    return () => clearInterval(t);
  }, [text, speed]);
  return <span>{displayed}</span>;
};

// ── Generating animation ──
const GeneratingScreen = () => {
  const steps = [
    { icon: BarChart3, label: 'Reading your task history…', color: '#00D9FF' },
    { icon: Brain, label: 'Analysing energy patterns…', color: '#a78bfa' },
    { icon: Heart, label: 'Assessing work-life balance…', color: '#ec4899' },
    { icon: Zap, label: 'Identifying burnout signals…', color: '#f97316' },
    { icon: Target, label: 'Building your action plan…', color: '#22c55e' },
    { icon: Sparkles, label: 'Crafting your life report…', color: '#eab308' },
  ];
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep(s => (s + 1) % steps.length), 1400);
    return () => clearInterval(t);
  }, []);
  const S = steps[step];
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-8">
      <div className="relative">
        <div className="w-24 h-24 rounded-full flex items-center justify-center"
          style={{ background: `${S.color}10`, border: `2px solid ${S.color}30`, boxShadow: `0 0 40px ${S.color}25` }}>
          <S.icon size={36} style={{ color: S.color }} className="animate-pulse"/>
        </div>
        <div className="absolute inset-0 rounded-full border-2 border-t-transparent animate-spin"
          style={{ borderColor: `${S.color}40`, borderTopColor: 'transparent' }}/>
      </div>
      <div className="text-center">
        <p className="text-white font-semibold text-lg mb-2">Generating your Life Intelligence Report</p>
        <p className="text-gray-400 text-sm min-h-[20px] transition-all">{S.label}</p>
      </div>
      <div className="flex gap-2">
        {steps.map((_, i) => (
          <div key={i} className="w-1.5 h-1.5 rounded-full transition-all"
            style={{ background: i === step ? S.color : 'rgba(255,255,255,0.15)' }}/>
        ))}
      </div>
    </div>
  );
};

// ── Main Report View ──
const ReportView = ({ report, meta, onRegenerate }) => {
  const r = report;
  const plan = r.next_month_action_plan || {};

  return (
    <div className="space-y-0">

      {/* Hero header */}
      <div className="glass-card p-8 mb-4 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5"
          style={{ background: 'radial-gradient(circle at 80% 50%, #a78bfa, transparent 60%), radial-gradient(circle at 20% 50%, #00D9FF, transparent 60%)' }}/>
        <div className="relative">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg,#a78bfa,#7c3aed)' }}>
              <Brain size={15} color="#fff"/>
            </div>
            <span className="text-xs font-semibold text-purple-400 uppercase tracking-wider">Monthly Life Intelligence Report</span>
            <span className="ml-auto text-xs text-gray-600">{meta.month}</span>
          </div>
          <p className="text-white text-lg leading-relaxed font-light">
            <TypeWriter text={r.opening_message || ''} speed={14}/>
          </p>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <StatCard label="Hours scheduled" value={meta.total_hours} suffix="h" icon={Clock} color="#00D9FF"/>
        <StatCard label="Completion rate" value={meta.completion_rate} suffix="%" icon={CheckCircle} color="#22c55e"/>
        <StatCard label="Burnout score" value={meta.burnout_score} suffix="/100" icon={Zap} color={meta.burnout_score > 50 ? '#f97316' : '#22c55e'}/>
        <div className="glass-card p-4 flex flex-col items-center text-center gap-2">
          <BurnoutRing score={meta.burnout_score}/>
        </div>
      </div>

      {/* What happened */}
      <ReportSection icon={BarChart3} title="What Happened This Month" color="#00D9FF">
        <p className="text-gray-300 text-sm leading-relaxed">{r.what_happened}</p>
      </ReportSection>

      {/* Why it happened */}
      <ReportSection icon={Brain} title="Why It Happened" color="#a78bfa">
        <p className="text-gray-300 text-sm leading-relaxed">{r.why_it_happened}</p>
      </ReportSection>

      {/* Energy */}
      <ReportSection icon={Zap} title="Your Energy Patterns" color="#eab308">
        <p className="text-gray-300 text-sm leading-relaxed">{r.energy_analysis}</p>
      </ReportSection>

      {/* Work-life */}
      <ReportSection icon={Heart} title="Work-Life Balance" color="#ec4899">
        <p className="text-gray-300 text-sm leading-relaxed">{r.work_life_balance}</p>
      </ReportSection>

      {/* Positive + Warning patterns */}
      <ReportSection icon={TrendingUp} title="Patterns Identified" color="#22c55e">
        <div className="mb-4">
          <p className="text-xs text-green-400 uppercase tracking-wider font-semibold mb-2">✅ What's working</p>
          {(r.positive_patterns || []).map((p, i) => <PatternPill key={i} text={p} type="positive"/>)}
        </div>
        <div>
          <p className="text-xs text-orange-400 uppercase tracking-wider font-semibold mb-2">⚠️ Watch these</p>
          {(r.warning_patterns || []).map((p, i) => <PatternPill key={i} text={p} type="warning"/>)}
        </div>
      </ReportSection>

      {/* Burnout */}
      <ReportSection icon={AlertTriangle} title="Burnout Assessment" color="#f97316" defaultOpen={meta.burnout_score > 40}>
        <p className="text-gray-300 text-sm leading-relaxed">{r.burnout_assessment}</p>
      </ReportSection>

      {/* Relationship with time */}
      <ReportSection icon={Clock} title="Your Relationship With Time" color="#06b6d4">
        <p className="text-gray-300 text-sm leading-relaxed">{r.relationship_with_time}</p>
      </ReportSection>

      {/* Next Month Action Plan */}
      <div className="glass-card overflow-hidden mb-4"
        style={{ border: '1px solid rgba(167,139,250,0.25)', background: 'rgba(167,139,250,0.04)' }}>
        <div className="px-6 py-5 border-b border-white/5"
          style={{ background: 'linear-gradient(135deg, rgba(167,139,250,0.1), rgba(0,217,255,0.05))' }}>
          <div className="flex items-center gap-3 mb-2">
            <Target size={18} className="text-purple-400"/>
            <span className="text-sm font-bold text-white">Next Month Action Plan</span>
            <Star size={14} className="text-yellow-400 ml-auto"/>
          </div>
          {plan.theme && (
            <p className="text-purple-300 text-sm font-medium italic">"{plan.theme}"</p>
          )}
        </div>
        <div className="p-6 grid sm:grid-cols-2 gap-6">
          <div>
            <p className="text-xs font-semibold text-cyan-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Calendar size={11}/> Schedule Improvements
            </p>
            {(plan.schedule_improvements || []).map((item, i) => <ActionItem key={i} text={item} index={i+1} color="#00D9FF"/>)}
          </div>
          <div>
            <p className="text-xs font-semibold text-purple-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <TrendingUp size={11}/> Habit Adjustments
            </p>
            {(plan.habit_adjustments || []).map((item, i) => <ActionItem key={i} text={item} index={i+1} color="#a78bfa"/>)}
          </div>
          <div>
            <p className="text-xs font-semibold text-pink-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Heart size={11}/> Recovery Blocks
            </p>
            {(plan.recovery_blocks || []).map((item, i) => <ActionItem key={i} text={item} index={i+1} color="#ec4899"/>)}
          </div>
          <div>
            <p className="text-xs font-semibold text-green-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Target size={11}/> Goal Actions
            </p>
            {(plan.goal_actions || []).map((item, i) => <ActionItem key={i} text={item} index={i+1} color="#22c55e"/>)}
          </div>
        </div>
      </div>

      {/* Closing */}
      <div className="glass-card p-6 text-center"
        style={{ background: 'linear-gradient(135deg, rgba(0,217,255,0.05), rgba(167,139,250,0.05))', border: '1px solid rgba(167,139,250,0.15)' }}>
        <Sparkles size={20} className="text-purple-400 mx-auto mb-3"/>
        <p className="text-gray-200 text-sm leading-relaxed italic">"{r.closing_message}"</p>
      </div>

      {/* Regenerate */}
      <div className="flex justify-center pt-4">
        <button onClick={onRegenerate}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs text-gray-500 hover:text-gray-300 transition-colors"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <RefreshCw size={12}/> Regenerate report
        </button>
      </div>
    </div>
  );
};

// ── Main Page ──
const LifeIntelligenceReport = () => {
  const { auth } = useContext(AuthContext);
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [report, setReport] = useState(null);
  const [meta, setMeta] = useState(null);
  const [history, setHistory] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [proLock, setProLock] = useState(null);
  const reportRef = useRef();

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API}/life-report/history`, { headers: { Authorization: `Bearer ${auth.token}` } });
      setHistory(res.data || []);
    } catch (e) { if (e.response?.status === 402) setProLock(e.response.data.detail); }
    finally { setLoadingHistory(false); }
  };

  const loadCached = async (year, month) => {
    try {
      const res = await axios.get(`${API}/life-report/${year}/${month}`, { headers: { Authorization: `Bearer ${auth.token}` } });
      setReport(res.data.report);
      setMeta(res.data.meta);
    } catch (e) {
      if (e.response?.status !== 404) toast.error('Could not load report');
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setReport(null);
    try {
      const res = await axios.post(`${API}/life-report/generate`,
        { month: selectedMonth, year: selectedYear },
        { headers: { Authorization: `Bearer ${auth.token}` } }
      );
      setReport(res.data.report);
      setMeta(res.data.meta);
      loadHistory();
      setTimeout(() => reportRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
    } catch (e) {
      toast.error('Could not generate report. Try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleHistoryClick = async (item) => {
    setReport(null);
    setSelectedMonth(item.month);
    setSelectedYear(item.year);
    await loadCached(item.year, item.month);
  };

  const hasHistoryForSelected = history.some(h => h.month === selectedMonth && h.year === selectedYear);
  const prevMonths = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return { month: d.getMonth() + 1, year: d.getFullYear(), label: MONTHS[d.getMonth()] + ' ' + d.getFullYear() };
  });

  if (proLock) return <ProUpgradeCard feature="Life Intelligence" message={proLock.message} />;

  return (
    <div className="min-h-screen pt-20 px-4 pb-16" style={{ background: 'linear-gradient(135deg,#0A0E27,#050816)' }}>
      <div className="max-w-2xl mx-auto">

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg,#a78bfa,#7c3aed)', boxShadow: '0 4px 20px rgba(167,139,250,0.4)' }}>
              <Brain size={18} color="#fff"/>
            </div>
            <div>
              <h1 className="heading-font text-2xl font-bold text-white flex items-center gap-2">Life Intelligence Report <ProBadge testId="page-pro-badge-life-report"/></h1>
              <div className="flex items-center gap-1.5 mt-0.5">
                <Star size={10} className="text-yellow-400"/>
                <span className="text-xs text-yellow-400 font-semibold">Pro Feature</span>
              </div>
            </div>
          </div>
          <p className="text-gray-400 text-sm mt-2">A monthly conversation with your AI life coach — not just what you did, but why, and what it means for who you're becoming.</p>
        </div>

        {/* Month selector + generate */}
        <div className="glass-card p-5 mb-6">
          <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Select month to analyse</p>
          <div className="flex gap-3 mb-4">
            <select value={selectedMonth} onChange={e => setSelectedMonth(Number(e.target.value))}
              className="flex-1 rounded-xl px-3 py-2.5 text-sm text-white outline-none"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}>
              {MONTHS.map((m, i) => <option key={m} value={i+1} style={{ background: '#0A0E27' }}>{m}</option>)}
            </select>
            <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))}
              className="w-28 rounded-xl px-3 py-2.5 text-sm text-white outline-none"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}>
              {[now.getFullYear(), now.getFullYear() - 1].map(y => <option key={y} value={y} style={{ background: '#0A0E27' }}>{y}</option>)}
            </select>
          </div>

          {hasHistoryForSelected && !generating && !report && (
            <div className="flex items-center gap-2 p-2.5 rounded-xl text-xs text-cyan-400 mb-3"
              style={{ background: 'rgba(0,217,255,0.06)', border: '1px solid rgba(0,217,255,0.15)' }}>
              <CheckCircle size={12}/> Report exists for this month.
              <button onClick={() => loadCached(selectedYear, selectedMonth)} className="underline ml-auto">Load it</button>
            </div>
          )}

          <button onClick={handleGenerate} disabled={generating}
            className="w-full teal-btn py-3 text-sm font-bold flex items-center justify-center gap-2 rounded-xl"
            style={{ background: 'linear-gradient(135deg,#a78bfa,#7c3aed)', boxShadow: '0 4px 20px rgba(167,139,250,0.35)', color: '#fff' }}>
            {generating ? <Loader2 size={16} className="animate-spin"/> : <Brain size={16}/>}
            {generating ? 'Generating report…' : hasHistoryForSelected ? 'Regenerate Report' : `Generate ${MONTHS[selectedMonth-1]} Report`}
          </button>
        </div>

        {/* Generating animation */}
        {generating && <GeneratingScreen/>}

        {/* Report output */}
        {report && meta && !generating && (
          <div ref={reportRef}>
            <ReportView report={report} meta={meta} onRegenerate={handleGenerate}/>
          </div>
        )}

        {/* History */}
        {!generating && !report && history.length > 0 && (
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Previous Reports</p>
            <div className="space-y-2">
              {history.map(item => (
                <button key={`${item.year}-${item.month}`} onClick={() => handleHistoryClick(item)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl text-left hover:bg-white/5 transition-colors"
                  style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.2)' }}>
                    <Brain size={16} className="text-purple-400"/>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-white">{MONTHS[item.month-1]} {item.year}</p>
                    {item.meta && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {item.meta.completion_rate}% completion · {item.meta.total_hours}h · Burnout: {item.meta.burnout_score}/100
                      </p>
                    )}
                  </div>
                  <ArrowRight size={14} className="text-gray-600"/>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!generating && !report && history.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
              style={{ background: 'linear-gradient(135deg, rgba(167,139,250,0.15), rgba(0,217,255,0.08))', border: '1px solid rgba(167,139,250,0.2)' }}>
              <Sparkles size={28} className="text-purple-400"/>
            </div>
            <p className="text-white font-semibold mb-2">Your first Life Intelligence Report</p>
            <p className="text-gray-500 text-sm max-w-xs mx-auto">Select a month above and generate your first AI-powered life review. It takes about 10 seconds.</p>
          </div>
        )}

      </div>
    </div>
  );
};

export default LifeIntelligenceReport;
